import { useI18n, useT, type Translate } from "../../../shared/i18n/i18n";
import { ChevronDown, Copy, CornerUpLeft, Paperclip, Pencil, Smile, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { formatRelativeTime } from "../../../shared/lib/format-relative-time";
import { cn } from "../../../shared/lib/cn";
import { EmojiPicker, quickReactionEmoji } from "../../../shared/ui/emoji-picker";
import { UserAvatar } from "../../../shared/ui/user-avatar";
import { attachmentUrl } from "../../../shared/api/attachments";
import type { WorkObjectAuditEvent } from "../model/use-work-object-audit";
import type { WorkObjectComment } from "../model/use-work-object-comments";
import { CommentBody } from "./comment-body";
import { CommentComposer, formatFileSize, type MentionMember } from "./comment-composer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";

type WorkObjectActivityProps = {
  auditEvents: WorkObjectAuditEvent[];
  comments: WorkObjectComment[];
  currentUserId: string | null;
  isLoading: boolean;
  members: MentionMember[];
  onDeleteComment: (commentId: string) => Promise<void>;
  onReply: (comment: WorkObjectComment) => void;
  onToggleReaction: (commentId: string, emoji: string) => Promise<void>;
  onUpdateComment: (input: { attachmentIds: string[]; body: Record<string, unknown>; commentId: string }) => Promise<void>;
  workObjectId: string;
};

type FeedItem =
  | { at: number; comment: WorkObjectComment; key: string; kind: "comment" }
  | { at: number; event: WorkObjectAuditEvent; key: string; kind: "audit" };

// Consecutive same-field edits by one actor within this window read as one change.
const AUDIT_COLLAPSE_WINDOW_MS = 5 * 60 * 1000;

export function WorkObjectActivity({
  auditEvents,
  comments,
  currentUserId,
  isLoading,
  members,
  onDeleteComment,
  onReply,
  onToggleReaction,
  onUpdateComment,
  workObjectId,
}: WorkObjectActivityProps) {
  const t = useT();
  const { locale } = useI18n();
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WorkObjectComment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const memberLabelById = useMemo(() => new Map(members.map((member) => [member.id, member.label])), [members]);

  const items = useMemo<FeedItem[]>(() => {
    const merged: FeedItem[] = [
      ...auditEvents.map((event) => ({
        at: new Date(event.createdAt).getTime(),
        event,
        key: `audit:${event.id}`,
        kind: "audit" as const,
      })),
      ...comments.map((comment) => ({
        at: new Date(comment.createdAt).getTime(),
        comment,
        key: `comment:${comment.id}`,
        kind: "comment" as const,
      })),
    ];
    merged.sort((a, b) => a.at - b.at);

    // Collapse a burst of edits to the same field by the same actor into one
    // row: keep the first "before" and the latest "after"/time, so five quick
    // tweaks to "Диапазон дат" read as a single net change, not five lines.
    const collapsed: FeedItem[] = [];
    for (const item of merged) {
      const previous = collapsed[collapsed.length - 1];
      if (
        item.kind === "audit" &&
        previous?.kind === "audit" &&
        // Only value edits collapse; a status change or an archive is its own
        // event even when it lands in the same minute.
        item.event.eventType === "field_value_changed" &&
        previous.event.eventType === "field_value_changed" &&
        previous.event.actorUserId === item.event.actorUserId &&
        previous.event.subjectId === item.event.subjectId &&
        item.at - previous.at <= AUDIT_COLLAPSE_WINDOW_MS
      ) {
        collapsed[collapsed.length - 1] = {
          ...item,
          event: { ...item.event, before: previous.event.before },
        };
        continue;
      }
      collapsed.push(item);
    }

    // Drop net no-ops (A → B → A within the window): nothing actually changed.
    // Only value edits carry before/after, so nothing else is judged this way.
    return collapsed.filter(
      (item) =>
        item.kind !== "audit" ||
        item.event.eventType !== "field_value_changed" ||
        formatAuditValue(item.event.before, item.event.valueLabels) !== formatAuditValue(item.event.after, item.event.valueLabels),
    );
  }, [auditEvents, comments]);

  const lastCommentId = comments.at(-1)?.id ?? null;
  useEffect(() => {
    const container = scrollRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [workObjectId, isLoading, lastCommentId]);

  useEffect(() => {
    setEditingCommentId(null);
  }, [workObjectId]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await onDeleteComment(pendingDelete.id);
      setPendingDelete(null);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3" ref={scrollRef}>
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-3/4" />
        </div>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t("item.comments.empty")}</p>
      ) : (
        <div className="flex flex-col gap-3.5">
          {items.map((item) =>
            item.kind === "audit" ? (
              <AuditRow event={item.event} key={item.key} memberLabelById={memberLabelById} />
            ) : (
              <CommentCard
                comment={item.comment}
                currentUserId={currentUserId}
                isEditing={editingCommentId === item.comment.id}
                key={item.key}
                memberLabelById={memberLabelById}
                members={members}
                onDelete={() => setPendingDelete(item.comment)}
                onEdit={() => setEditingCommentId(item.comment.id)}
                onFinishEdit={() => setEditingCommentId(null)}
                onReply={() => onReply(item.comment)}
                onToggleReaction={(emoji) => onToggleReaction(item.comment.id, emoji)}
                onUpdate={onUpdateComment}
                workObjectId={workObjectId}
              />
            ),
          )}
        </div>
      )}

      <AlertDialog onOpenChange={(open) => (open ? undefined : setPendingDelete(null))} open={pendingDelete !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("item.comments.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>{t("item.comments.deleteDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={isDeleting} onClick={() => void confirmDelete()} variant="destructive">
              {isDeleting ? t("itemComments.deleting") : t("item.comments.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AuditRow({
  event,
  memberLabelById,
}: {
  event: WorkObjectAuditEvent;
  memberLabelById: Map<string, string>;
}) {
  const t = useT();
  const { locale } = useI18n();
  const actor = event.actorUserId ? memberLabelById.get(event.actorUserId) ?? event.actorEmail ?? t("common.someone") : "Impacto";
  return (
    <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
      <span className="mt-1 size-1.5 shrink-0 self-start rounded-full bg-border" />
      <p className="min-w-0 flex-1 [overflow-wrap:anywhere]">
        <span className="font-medium text-foreground/75">{actor}</span> {describeAuditEvent(event, t)}
      </p>
      <time className="shrink-0" title={new Date(event.createdAt).toLocaleString()}>
        {formatRelativeTime(event.createdAt, t, locale)}
      </time>
    </div>
  );
}

// The activity log now carries more than field edits, so each kind gets a line
// that reads as a sentence instead of an empty "changed a field".
// The history reads as a sentence, so the sentence has to be translatable —
// not assembled from English fragments around a translated noun.
function describeAuditEvent(event: WorkObjectAuditEvent, t: Translate) {
  switch (event.eventType) {
    case "work_object_created":
      return <>{t("item.activityCreated")}</>;
    case "work_object_archived":
      return <>{t("item.activityArchived")}</>;
    case "work_object_restored":
      return <>{t("item.activityRestored")}</>;
    case "status_changed":
      return (
        <>
          {t("item.activityMovedTo")}{" "}
          <span className="font-medium text-foreground/75">{formatAuditValue(event.after, event.valueLabels, t)}</span>
          {event.before ? (
            <>
              {" "}
              {t("item.activityFrom")} {formatAuditValue(event.before, event.valueLabels, t)}
            </>
          ) : null}
        </>
      );
    case "sprint_scope_added":
      return <>{t("item.activitySprintAdded")}</>;
    case "sprint_scope_removed":
      return <>{t("item.activitySprintRemoved")}</>;
    default:
      return (
        <>
          {t("item.activityChanged")}{" "}
          <span className="font-medium text-foreground/75">{event.subjectName ?? t("item.activityField")}</span>
          {": "}
          {formatAuditValue(event.before, event.valueLabels, t)} → {formatAuditValue(event.after, event.valueLabels, t)}
        </>
      );
  }
}

function CommentCard({
  comment,
  currentUserId,
  isEditing,
  memberLabelById,
  members,
  onDelete,
  onEdit,
  onFinishEdit,
  onReply,
  onToggleReaction,
  onUpdate,
  workObjectId,
}: {
  comment: WorkObjectComment;
  currentUserId: string | null;
  isEditing: boolean;
  memberLabelById: Map<string, string>;
  members: MentionMember[];
  onDelete: () => void;
  onEdit: () => void;
  onFinishEdit: () => void;
  onReply: () => void;
  onToggleReaction: (emoji: string) => Promise<void>;
  onUpdate: (input: { attachmentIds: string[]; body: Record<string, unknown>; commentId: string }) => Promise<void>;
  workObjectId: string;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuPickerExpanded, setIsMenuPickerExpanded] = useState(false);
  const authorLabel = comment.author.displayName ?? comment.author.email ?? "Unknown";
  const isOwn = currentUserId !== null && comment.author.id === currentUserId;
  const images = comment.attachments.filter((attachment) => attachment.contentType.startsWith("image/"));
  const files = comment.attachments.filter((attachment) => !attachment.contentType.startsWith("image/"));

  function handleDoubleClick(event: ReactMouseEvent<HTMLElement>) {
    const target = event.target as HTMLElement;
    if (target.closest("a, button, img")) return;
    window.getSelection()?.removeAllRanges();
    void onToggleReaction("👍");
  }

  function pickMenuReaction(emoji: string) {
    void onToggleReaction(emoji);
    setIsMenuOpen(false);
  }

  if (isEditing) {
    return (
      <div id={`comment-${comment.id}`}>
        <CommentComposer
          autoFocus
          initialAttachments={comment.attachments.map((attachment) => ({
            contentType: attachment.contentType,
            downloadKey: attachment.downloadKey,
            fileName: attachment.fileName,
            id: attachment.id,
            sizeBytes: attachment.sizeBytes,
          }))}
          initialBody={comment.body}
          members={members}
          onCancel={onFinishEdit}
          onSubmit={async ({ attachmentIds, body }) => {
            await onUpdate({ attachmentIds, body, commentId: comment.id });
            onFinishEdit();
          }}
          placeholder={t("item.comments.editPlaceholder")}
          submitLabel={t("common.save")}
          workObjectId={workObjectId}
        />
      </div>
    );
  }

  return (
    <ContextMenu
      onOpenChange={(open) => {
        setIsMenuOpen(open);
        if (!open) setIsMenuPickerExpanded(false);
      }}
      open={isMenuOpen}
    >
      <ContextMenuTrigger
        className="group flex select-auto gap-2.5"
        id={`comment-${comment.id}`}
        onDoubleClick={handleDoubleClick}
        render={<div />}
      >
      <UserAvatar className="mt-0.5 size-7 text-[0.6rem]" name={authorLabel} seed={comment.author.id} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 truncate text-sm font-medium">{authorLabel}</span>
          <time className="shrink-0 text-xs text-muted-foreground" title={new Date(comment.createdAt).toLocaleString()}>
            {formatRelativeTime(comment.createdAt, t, locale)}
          </time>
          {comment.editedAt ? (
            <span className="shrink-0 text-xs text-muted-foreground/70" title={new Date(comment.editedAt).toLocaleString()}>
              (edited)
            </span>
          ) : null}
          <div className="ml-auto flex shrink-0 items-center opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            <ReactionPickerButton onPick={(emoji) => void onToggleReaction(emoji)} />
            <Button aria-label={t("item.comments.reply")} className="size-6 text-muted-foreground" onClick={onReply} size="icon-sm" variant="ghost">
              <CornerUpLeft className="size-3.5" />
            </Button>
            {isOwn ? (
              <>
                <Button aria-label={t("item.comments.edit")} className="size-6 text-muted-foreground" onClick={onEdit} size="icon-sm" variant="ghost">
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  aria-label={t("item.comments.delete")}
                  className="size-6 text-muted-foreground hover:text-destructive"
                  onClick={onDelete}
                  size="icon-sm"
                  variant="ghost"
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </>
            ) : null}
          </div>
        </div>

        {comment.replyTo ? (
          <button
            className="mt-1 flex w-full items-baseline gap-1.5 rounded-md border-l-2 border-border bg-muted/30 px-2 py-1 text-left text-xs hover:bg-muted/50"
            onClick={() => {
              document.getElementById(`comment-${comment.replyTo?.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            type="button"
          >
            <span className="shrink-0 font-medium text-muted-foreground">
              {comment.replyTo.authorDisplayName ?? comment.replyTo.authorEmail ?? "Unknown"}
            </span>
            <span className="min-w-0 truncate text-muted-foreground/80">{comment.replyTo.bodyText || "Attachment"}</span>
          </button>
        ) : null}

        <div className="mt-0.5">
          <CommentBody body={comment.body} />
        </div>

        {images.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {images.map((attachment) => (
              <a href={attachmentUrl(attachment)} key={attachment.id} rel="noreferrer" target="_blank">
                <img
                  alt={attachment.fileName}
                  className="max-h-44 max-w-full rounded-md border border-border/60 object-cover"
                  loading="lazy"
                  src={attachmentUrl(attachment)}
                />
              </a>
            ))}
          </div>
        ) : null}

        {files.length > 0 ? (
          <div className="mt-1.5 flex flex-col gap-1">
            {files.map((attachment) => (
              <a
                className="flex items-center gap-1.5 self-start rounded-md border border-border/60 bg-background px-2 py-1.5 text-xs hover:bg-accent/50"
                href={attachmentUrl(attachment)}
                key={attachment.id}
                rel="noreferrer"
                target="_blank"
              >
                <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 max-w-56 truncate font-medium">{attachment.fileName}</span>
                <span className="shrink-0 text-muted-foreground">{formatFileSize(attachment.sizeBytes)}</span>
              </a>
            ))}
          </div>
        ) : null}

        {comment.reactions.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {comment.reactions.map((reaction) => {
              const isMine = currentUserId !== null && reaction.userIds.includes(currentUserId);
              const names = reaction.userIds.map((userId) => memberLabelById.get(userId) ?? "Unknown").join(", ");
              return (
                <button
                  className={cn(
                    "flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors",
                    isMine ? "border-primary/40 bg-primary/10" : "border-border/60 bg-background hover:bg-accent/50",
                  )}
                  key={reaction.emoji}
                  onClick={() => void onToggleReaction(reaction.emoji)}
                  title={names}
                  type="button"
                >
                  <span>{reaction.emoji}</span>
                  <span className="tabular-nums text-muted-foreground">{reaction.userIds.length}</span>
                </button>
              );
            })}
            <ReactionPickerButton className="opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100" onPick={(emoji) => void onToggleReaction(emoji)} />
          </div>
        ) : null}
      </div>
      </ContextMenuTrigger>

      <ContextMenuContent className="p-0">
        {isMenuPickerExpanded ? (
          <EmojiPicker onPick={pickMenuReaction} />
        ) : (
          <div className="min-w-48 p-1">
            <div className="flex items-center gap-0.5 pb-1">
              {quickReactionEmoji.map((emoji) => (
                <button
                  className="grid size-8 place-items-center rounded-md text-lg transition-colors hover:bg-accent"
                  key={emoji}
                  onClick={() => pickMenuReaction(emoji)}
                  type="button"
                >
                  {emoji}
                </button>
              ))}
              <button
                aria-label={t("item.comments.moreReactions")}
                className="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                onClick={() => setIsMenuPickerExpanded(true)}
                type="button"
              >
                <ChevronDown className="size-4" />
              </button>
            </div>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onReply}>
              <CornerUpLeft /> {t("item.comments.reply")}
            </ContextMenuItem>
            <ContextMenuItem onClick={() => void navigator.clipboard.writeText(comment.bodyText)}>
              <Copy /> {t("item.comments.copyText")}
            </ContextMenuItem>
            {isOwn ? (
              <>
                <ContextMenuItem onClick={onEdit}>
                  <Pencil /> {t("common.edit")}
                </ContextMenuItem>
                <ContextMenuItem onClick={onDelete} variant="destructive">
                  <Trash2 /> {t("common.delete")}
                </ContextMenuItem>
              </>
            ) : null}
          </div>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

function ReactionPickerButton({ className, onPick }: { className?: string; onPick: (emoji: string) => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        render={
          <Button aria-label={t("item.comments.addReaction")} className={cn("size-6 text-muted-foreground", className)} size="icon-sm" variant="ghost">
            <Smile className="size-3.5" />
          </Button>
        }
      />
      <PopoverContent align="end" className="w-auto p-0">
        <EmojiPicker
          onPick={(emoji) => {
            onPick(emoji);
            setIsOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

// Relation, person and select values are stored as ids; the server ships the
// label for each one, so the trail reads as names rather than UUIDs.
function formatAuditValue(value: unknown, labels: Record<string, string> = {}, t?: Translate) {
  const empty = t ? t("item.activityEmpty") : "empty";
  if (value === null || value === undefined || value === "") return empty;
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map((entry) => labels[String(entry)] ?? String(entry)).join(", ") : empty;
  }
  if (typeof value === "object") return JSON.stringify(value);
  return labels[String(value)] ?? String(value);
}
